import axios from 'axios';
import Cookies from 'js-cookie';
import { CONFIG } from '@/config/Config';
import { CAROUSEL_MAX_ITEMS, CAROUSEL_MIN_ITEMS } from '@/features/scheduler/utils/carouselMedia';

export interface CreateContainerRequest {
  file: File;
  fileType: 'image' | 'video';
  scheduledPublishTime: string;
  caption?: string;
  shareToFeed?: boolean;
  mediaType: 'STORIES' | 'REELS' | 'IMAGE' | 'CAROUSEL';
  userTags?: string[];
  mediaUrl?: string;
  reelsCover?: ReelsCoverOptions;
  isCarouselItem?: boolean;
}

export type ReelsCoverOptions = { cover: File };

export interface SchedulePostOptions {
  reelsCover?: ReelsCoverOptions;
  shareToFeed?: boolean;
}

export interface ContainerResponse {
  id: string;
}

export interface CarouselContainerPayload {
  image_url?: string;
  video_url?: string;
  media_type?: string;
  caption?: string;
  children?: string[];
  is_ai_generated?: boolean;
  [key: string]: unknown;
}

export interface CreateContainerResponse {
  containerResponse?: ContainerResponse;
  rawGraphContainerPayload?: CarouselContainerPayload;
  data?: CreateContainerResponse;
  [key: string]: any;
}

export interface CarouselScheduleItem {
  id: string;
  name: string;
  file: File;
  fileType: 'image' | 'video';
  isAiGenerated: boolean;
  containerId?: string;
  containerPayload?: CarouselContainerPayload;
  containerCreatedAt?: number;
  retryStatus?: string;
}

export interface CarouselBatchItemResult {
  index: number;
  status: string;
  containerId?: string;
  rawGraphContainerPayload?: CarouselContainerPayload;
  error?: string;
}

export interface CarouselBatchResponse {
  status: string;
  items: CarouselBatchItemResult[];
  parentStatus?: string;
  parentContainerId?: string;
  parentError?: string;
  parentPayload?: CarouselContainerPayload;
  unknownContainerCreationCount?: number;
}

export interface CarouselItemProgress {
  status: 'ready' | 'error';
  message?: string;
  retryStatus: string;
  containerId?: string;
  containerPayload?: CarouselContainerPayload;
  containerCreatedAt?: number;
}

export interface CarouselParentProgress {
  containerId: string;
  payload: CarouselContainerPayload;
}

const HTTP_SERVER_ERROR_THRESHOLD = 500;

export class CarouselScheduleError extends Error {
  constructor(
    message: string,
    public readonly outcomeUnknown: boolean,
    public readonly stillProcessing: boolean = false,
  ) {
    super(message);
    this.name = 'CarouselScheduleError';
  }
}

export interface ScheduleCarouselOptions {
  fbIgId: string;
  items: CarouselScheduleItem[];
  scheduleType: 'now' | 'scheduled';
  caption?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  isAiGenerated: boolean;
  parentContainerId?: string;
  parentPayload?: CarouselContainerPayload;
  onItemProgress?: (itemId: string, progress: CarouselItemProgress) => void;
  onParentProgress?: (progress: CarouselParentProgress) => void;
}

export interface SaveScheduledRequest {
  container_ids?: string[];
  page_id: string;
  scheduled_to: string;
  caption?: string;
  media_type: 'STORIES' | 'REELS' | 'IMAGE' | 'CAROUSEL';
  share_to_feed?: boolean;
  media_url?: string;
  children?: string[];
  carousel_items?: CarouselContainerPayload[];
  raw_data?: CarouselContainerPayload;
}

export const CAROUSEL_CONTAINER_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface SaveScheduledResponse {
  success?: boolean;
  status?: string;
  message?: string;
  error?: string;
  scheduledPostId?: string;
  [key: string]: any;
}

const getScheduleErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError<{ message?: string; error?: string }>(error)) {
    return error.response?.data?.message ?? error.response?.data?.error ?? error.message;
  }
  return error instanceof Error ? error.message : 'Unknown carousel scheduling error';
};

class ScheduledPostService {
  private getBaseUrl(): string {
    return CONFIG.getServiceUrl('sac-reply');
  }

  private getAccountId(): string {
    const accId = CONFIG.cookieOrParam(CONFIG.AUTH.ACC_ID)?.split('|')[0];
    
    if (!accId) {
      throw new Error('Account ID not found. User must be authenticated.');
    }
    
    return accId;
  }

  private createAuthenticatedAxios() {
    const axiosInstance = axios.create();
    
    axiosInstance.interceptors.request.use((config) => {
      const token = Cookies.get(CONFIG.AUTH.SSID);

      if (token) {
        config.headers[CONFIG.AUTH.SSID] = token;
      }
      return config;
    });

    return axiosInstance;
  }

  private formatScheduledTime(date: string, time: string): string {
    // Convert DD/MM/YYYY and HH:MM to ISO_LOCAL_DATE_TIME format (2023-10-05T14:30:00)
    const [day, month, year] = date.split('/');
    const [hours, minutes] = time.split(':');
    
    // Return in ISO_LOCAL_DATE_TIME format without timezone info
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  }

  private getScheduledTimeForNow(): string {
    // Schedule for 1 minute from now in ISO_LOCAL_DATE_TIME format
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  /**
   * Creates a container for scheduled content
   */
  async createContainer(
    fbIgId: string,
    request: CreateContainerRequest
  ): Promise<CreateContainerResponse> {
    try {
      const accountId = this.getAccountId();
      const url = `${this.getBaseUrl()}/instagram/scheduledcontent/${accountId}/${fbIgId}/create-container`;

      const formData = new FormData();
      formData.append('file', request.file);
      formData.append('fileType', request.fileType);
      formData.append('scheduledPublishTime', request.scheduledPublishTime);
      formData.append('mediaType', request.mediaType);
      if (request.isCarouselItem) {
        formData.append('isCarouselItem', 'true');
      }
      
      // Caption is not allowed for STORIES
      if (request.caption && request.mediaType !== 'STORIES') {
        formData.append('caption', request.caption);
      }
      
      // shareToFeed is only allowed for REELS, not for STORIES or FEED
      if (request.shareToFeed !== undefined && request.mediaType === 'REELS') {
        formData.append('shareToFeed', String(request.shareToFeed));
      }

      if (request.mediaType === 'REELS' && request.reelsCover) {
        formData.append('cover', request.reelsCover.cover);
      }

      if (request.userTags && request.userTags.length > 0) {
        formData.append('userTags', JSON.stringify(request.userTags));
      }

      const axiosInstance = this.createAuthenticatedAxios();

      // Do not set Content-Type manually: the browser/axios must generate it
      // automatically for FormData, including the multipart boundary. Forcing
      // 'multipart/form-data' without a boundary breaks multipart parsing and
      // can corrupt non-ASCII characters (e.g. accents) sent in text fields.
      const response = await axiosInstance.post<CreateContainerResponse>(url, formData);

      return response.data;
    } catch (error) {
      console.error('Error creating container:', error);
      throw error;
    }
  }

  /**
   * Saves the scheduled post
   */
  async saveScheduled(
    fbIgId: string,
    request: SaveScheduledRequest
  ): Promise<SaveScheduledResponse> {
    try {
      const accountId = this.getAccountId();
      const url = `${this.getBaseUrl()}/instagram/scheduledcontent/${accountId}/${fbIgId}/save-scheduled`;

      const axiosInstance = this.createAuthenticatedAxios();

      const response = await axiosInstance.post<SaveScheduledResponse>(url, request, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error saving scheduled post:', error);
      throw error;
    }
  }

  async createCarouselContainers(
    fbIgId: string,
    scheduledPublishTime: string,
    caption: string | undefined,
    isAiGenerated: boolean,
    items: CarouselScheduleItem[],
  ): Promise<CarouselBatchResponse> {
    const accountId = this.getAccountId();
    const formData = new FormData();
    formData.append('scheduledPublishTime', scheduledPublishTime);
    formData.append('isAiGenerated', String(isAiGenerated));
    if (caption) formData.append('caption', caption);

    items.forEach((item, index) => {
      const prefix = `items[${index}]`;
      const mediaType = item.fileType === 'image' ? 'IMAGE' : 'VIDEO';
      const reusable = this.canReuseCarouselItem(item);
      formData.append(`${prefix}.mediaType`, mediaType);
      formData.append(`${prefix}.isAiGenerated`, String(item.isAiGenerated));
      if (reusable) {
        formData.append(`${prefix}.existingContainerId`, item.containerId!);
        formData.append(`${prefix}.existingMediaUrl`, this.getCarouselMediaUrl(item.containerPayload!));
      } else {
        formData.append(`${prefix}.file`, item.file);
      }
    });

    const url = `${this.getBaseUrl()}/instagram/scheduledcontent/${accountId}/${fbIgId}/create-carousel-containers`;
    try {
      // Do not set Content-Type manually: the browser/axios must generate it
      // automatically for FormData, including the multipart boundary. Forcing
      // 'multipart/form-data' without a boundary breaks multipart parsing and
      // can corrupt non-ASCII characters (e.g. accents) sent in text fields.
      const response = await this.createAuthenticatedAxios().post<{ data: CarouselBatchResponse } | CarouselBatchResponse>(
        url,
        formData,
      );
      const result = 'data' in response.data ? response.data.data : response.data;
      if (!result || !Array.isArray(result.items)) {
        throw new CarouselScheduleError('The backend returned an invalid carousel batch response', true);
      }
      return result;
    } catch (error) {
      if (error instanceof CarouselScheduleError) throw error;
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const unknownOutcome = status === undefined || status >= HTTP_SERVER_ERROR_THRESHOLD;
      throw new CarouselScheduleError(getScheduleErrorMessage(error), unknownOutcome);
    }
  }

  async scheduleCarouselPost(options: ScheduleCarouselOptions): Promise<SaveScheduledResponse> {
    if (options.items.length < CAROUSEL_MIN_ITEMS || options.items.length > CAROUSEL_MAX_ITEMS) {
      throw new RangeError('A carousel must contain between 2 and 10 items');
    }

    const scheduledPublishTime = options.scheduleType === 'now'
      ? this.getScheduledTimeForNow()
      : this.formatScheduledTime(options.scheduledDate!, options.scheduledTime!);
    let parentContainerId = options.parentContainerId;
    let parentPayload = options.parentPayload;
    let carouselPayloads = options.items
      .map((item) => item.containerPayload)
      .filter(Boolean) as CarouselContainerPayload[];

    if (!parentContainerId || !parentPayload) {
      const resolved = await this.ensureCarouselParent(options, scheduledPublishTime);
      parentContainerId = resolved.parentContainerId;
      parentPayload = resolved.parentPayload;
      carouselPayloads = resolved.carouselPayloads;
    }

    return this.submitCarouselSchedule(options, parentContainerId, parentPayload, carouselPayloads, scheduledPublishTime);
  }

  private async ensureCarouselParent(
    options: ScheduleCarouselOptions,
    scheduledPublishTime: string,
  ): Promise<{
    parentContainerId: string;
    parentPayload: CarouselContainerPayload;
    carouselPayloads: CarouselContainerPayload[];
  }> {
    const batch = await this.createCarouselContainers(
      options.fbIgId,
      scheduledPublishTime,
      options.caption,
      options.isAiGenerated,
      options.items,
    );
    this.reportCarouselItemProgress(options, batch);
    const carouselPayloads = batch.items
      .map((item) => item.rawGraphContainerPayload)
      .filter(Boolean) as CarouselContainerPayload[];

    this.assertCarouselBatchOutcomeKnown(batch);
    this.assertCarouselBatchComplete(batch, carouselPayloads, options.items.length);

    const parentPayload = { ...batch.parentPayload! };
    if (!options.isAiGenerated) delete parentPayload.is_ai_generated;
    options.onParentProgress?.({ containerId: batch.parentContainerId!, payload: parentPayload });

    return { parentContainerId: batch.parentContainerId!, parentPayload, carouselPayloads };
  }

  private assertCarouselBatchOutcomeKnown(batch: CarouselBatchResponse): void {
    const hasUnknownItem = batch.items.some((item) => item.status === 'UNKNOWN');
    if (hasUnknownItem || (batch.unknownContainerCreationCount ?? 0) > 0
      || batch.parentStatus === 'UNKNOWN' || batch.status === 'UNKNOWN'
      || batch.status === 'PARENT_CREATION_UNKNOWN') {
      throw new CarouselScheduleError(
        batch.parentError ?? 'The outcome of one or more carousel container creations is unknown',
        true,
      );
    }
  }

  private assertCarouselBatchComplete(
    batch: CarouselBatchResponse,
    carouselPayloads: CarouselContainerPayload[],
    expectedItemCount: number,
  ): void {
    const isComplete = batch.status === 'COMPLETE' && batch.parentContainerId && batch.parentPayload
      && carouselPayloads.length === expectedItemCount;
    if (isComplete) return;
    const hasFailedItem = batch.items.some((item) => item.status === 'FAILED');
    const blockingItem = batch.items.find((item) => item.status === 'FAILED' || item.status === 'PROCESSING');
    const errorMessage = batch.parentError
      ?? blockingItem?.error
      ?? 'Some carousel items are not ready. Retry the failed or processing items.';
    // When no item has definitively failed, the pending ones are simply still
    // being processed by Instagram (common for larger videos). Flag this so the
    // UI can show a reassuring message instead of the raw backend/API text.
    throw new CarouselScheduleError(errorMessage, false, !hasFailedItem && !batch.parentError);
  }

  private async submitCarouselSchedule(
    options: ScheduleCarouselOptions,
    parentContainerId: string,
    parentPayload: CarouselContainerPayload,
    carouselPayloads: CarouselContainerPayload[],
    scheduledPublishTime: string,
  ): Promise<SaveScheduledResponse> {
    let saveResponse: SaveScheduledResponse;
    try {
      saveResponse = await this.saveScheduled(options.fbIgId, {
        container_ids: [parentContainerId],
        children: parentPayload.children,
        carousel_items: carouselPayloads,
        page_id: options.fbIgId,
        scheduled_to: scheduledPublishTime,
        caption: options.caption,
        media_type: 'CAROUSEL',
        raw_data: parentPayload,
      });
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      throw new CarouselScheduleError(getScheduleErrorMessage(error), status === undefined || status >= HTTP_SERVER_ERROR_THRESHOLD);
    }

    const status = String(saveResponse.status ?? '').toUpperCase();
    if (saveResponse.success === false || ['FAILED', 'CANCELED', 'INVALID', 'VALIDATION_ERROR'].includes(status)) {
      throw new CarouselScheduleError(
        saveResponse.message ?? saveResponse.error ?? 'The carousel could not be scheduled',
        false,
      );
    }
    const isAcceptedStatus = ['SCHEDULED', 'PUBLISHED', 'PENDING', 'PROCESSING', 'RETRYING'].includes(status);
    if (!isAcceptedStatus && saveResponse.success !== true) {
      throw new CarouselScheduleError('The backend did not confirm the carousel scheduling result', true);
    }
    return saveResponse;
  }

  private canReuseCarouselItem(item: CarouselScheduleItem): boolean {
    if (!item.containerId || !item.containerPayload || !item.containerCreatedAt) return false;
    if (!item.containerPayload.image_url && !item.containerPayload.video_url) return false;
    return Date.now() - item.containerCreatedAt < CAROUSEL_CONTAINER_MAX_AGE_MS;
  }

  private getCarouselMediaUrl(payload: CarouselContainerPayload): string {
    const mediaUrl = payload.image_url ?? payload.video_url;
    if (!mediaUrl) throw new CarouselScheduleError('A reusable carousel item is missing its media URL', true);
    return mediaUrl;
  }

  private reportCarouselItemProgress(
    options: ScheduleCarouselOptions,
    batch: CarouselBatchResponse,
  ): void {
    batch.items.forEach((result) => {
      const submittedItem = options.items[result.index];
      if (!submittedItem) return;
      const isReusable = ['CREATED', 'REUSED', 'PROCESSING'].includes(result.status)
        && Boolean(result.containerId && result.rawGraphContainerPayload);
      options.onItemProgress?.(submittedItem.id, {
        status: 'ready',
        retryStatus: result.status,
        message: result.error,
        ...(isReusable ? {
          containerId: result.containerId,
          containerPayload: result.rawGraphContainerPayload,
          containerCreatedAt: submittedItem.containerCreatedAt ?? Date.now(),
        } : { containerId: undefined, containerPayload: undefined, containerCreatedAt: undefined }),
      });
    });
  }

  /**
   * Complete flow: create container and save scheduled post
   */
  async schedulePost(
    fbIgId: string,
    file: File,
    mediaType: 'STORIES' | 'REELS' | 'IMAGE' | 'CAROUSEL',
    scheduleType: 'now' | 'scheduled',
    caption?: string,
    scheduledDate?: string,
    scheduledTime?: string,
    userTags?: string[],
    options: SchedulePostOptions = {}
  ): Promise<SaveScheduledResponse> {
    try {
      // Determine scheduled time
      const scheduledPublishTime = scheduleType === 'now'
        ? this.getScheduledTimeForNow()
        : this.formatScheduledTime(scheduledDate!, scheduledTime!);

      // Determine file type
      const fileType: 'image' | 'video' = file.type.startsWith('image/') ? 'image' : 'video';

      // Determine shareToFeed (only for REELS)
      const reelsShareToFeed =
        mediaType === 'REELS' ? (options.shareToFeed ?? true) : undefined;

      // Step 1: Create container (backend will handle the upload)
      const containerResponse = await this.createContainer(fbIgId, {
        file,
        fileType,
        scheduledPublishTime,
        caption: mediaType !== 'STORIES' ? caption : undefined, // Caption not allowed for STORIES
        shareToFeed: reelsShareToFeed,
        mediaType: mediaType,
        userTags,
        reelsCover: mediaType === 'REELS' ? options.reelsCover : undefined,
      });

      const responseData = containerResponse?.data || containerResponse;

      if (!responseData) {
        throw new Error('Invalid container response structure');
      }

      const containerId = responseData.containerResponse?.id;

      if (!containerId) {
        throw new Error('Container ID not found in response');
      }

      const rawData = {
        ...(fileType === 'image'
          ? { image_url: responseData.rawGraphContainerPayload?.image_url || '' }
          : { video_url: responseData.rawGraphContainerPayload?.video_url || '' }),
        caption: mediaType !== 'STORIES' ? caption : undefined,
        media_type: responseData.rawGraphContainerPayload?.media_type || mediaType,
      };

      // Step 2: Save scheduled post with raw_data
      const saveResponse = await this.saveScheduled(fbIgId, {
        container_ids: [containerId],
        page_id: fbIgId,
        scheduled_to: scheduledPublishTime,
        caption: mediaType !== 'STORIES' ? caption : undefined,
        media_type: mediaType,
        ...(reelsShareToFeed === undefined ? {} : { share_to_feed: reelsShareToFeed }),
        raw_data: rawData,
      });

      return saveResponse;
    } catch (error) {
      console.error('Error scheduling post:', error);
      throw error;
    }
  }
}

const scheduledPostService = new ScheduledPostService();
export default scheduledPostService;